/*
  assets/gtender-dashboard-access.js — GTENDER-NATIVE-DASHBOARD-ACCESS-G15-01
  Owner: /pages/gtender-dashboard Visitor / GTender Free / GTender Pro projection.
  Identity authority: LittleGWebSession -> gcity-core-api native principal.
  Plan authority: backend gtender_account. No Shopify browser/session identity.
*/
(function () {
  'use strict';

  var VERSION = 'gtender-native-dashboard-access-g15-01';
  var ROOT_SELECTOR = '[data-gtender-dashboard-root][data-native-session-required]';
  var GLOBAL_KEY = '__GTENDER_DASHBOARD_ACCESS_RUNTIME__';
  var ACCESS_URL = 'https://api.glhksolution.com/v1/gtender/access';
  var ACCESS_TIMEOUT_MS = 10000;
  var PRO_RUNTIME_KEY = '__GTENDER_PRO_RUNTIME_LOADING__';

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].boot === 'function') {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  var stateByRoot = new WeakMap();
  var mounted = [];
  var observer = null;

  function list(root, selector) {
    return root && root.querySelectorAll
      ? Array.prototype.slice.call(root.querySelectorAll(selector))
      : [];
  }

  function documentOwns(node) {
    return !!(node && document.documentElement && document.documentElement.contains(node));
  }

  /**
   * @param {Element} node
   * @returns {node is HTMLElement}
   */
  function isHtmlElement(node) {
    return node instanceof HTMLElement;
  }

  /** @param {unknown} value */
  function normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase();
  }

  function show(root, name) {
    list(root, '[data-gtender-dashboard-access]').forEach(function (panel) {
      var isCurrent = panel.getAttribute('data-gtender-dashboard-access') === name;
      panel.hidden = !isCurrent;
      if (panel.getAttribute('data-gtender-dashboard-access') === 'checking') {
        panel.setAttribute('aria-busy', name === 'checking' ? 'true' : 'false');
      }
    });

    var content = root.querySelector('[data-gtender-dashboard-content]');
    if (content) content.hidden = name !== 'pro';

    if (name !== 'pro') {
      root.removeAttribute('data-pro-valid-until');
      root.removeAttribute('data-dashboard-render-state');
      var workspace = root.querySelector('[data-gtender-dashboard-list]');
      if (workspace) workspace.replaceChildren();
      list(root, '[data-gtender-dashboard-stat]').forEach(function (node) {
        node.textContent = '—';
      });
      list(root, '[data-gtender-workspace-panel] form').forEach(function (form) {
        if (typeof form.reset === 'function') form.reset();
      });
      root.dispatchEvent(new CustomEvent('gtender-dashboard:access-invalidated'));
    }
    root.setAttribute('data-access-state', name);
    if (name === 'free' || name === 'pro') {
      root.setAttribute('data-identity-state', 'authenticated');
      root.setAttribute('data-account-state', name);
      root.setAttribute('data-product-contract-state', 'connected');
    } else {
      root.removeAttribute('data-account-state');
      root.setAttribute(
        'data-product-contract-state',
        name === 'checking' ? 'checking' : 'not-applicable'
      );
      root.setAttribute(
        'data-identity-state',
        name === 'guest' ? 'anonymous' : name === 'checking' ? 'checking' : 'unavailable'
      );
    }
  }

  function locateProRuntimeUrl() {
    var scripts = Array.prototype.slice.call(document.scripts || []);
    var owner = scripts.find(function (script) {
      return /gtender-dashboard-access\.js(?:\?|$)/.test(String(script.src || ''));
    });
    if (!owner || !owner.src) return '';
    return owner.src.replace(/gtender-dashboard-access\.js(?=\?|$)/, 'gtender-dashboard.js');
  }

  function ensureProRuntime() {
    if (window.GTenderDashboard && typeof window.GTenderDashboard.boot === 'function') {
      window.GTenderDashboard.boot(document);
      return Promise.resolve(window.GTenderDashboard);
    }
    if (window[PRO_RUNTIME_KEY]) return window[PRO_RUNTIME_KEY];

    var src = locateProRuntimeUrl();
    if (!src) return Promise.reject(new Error('GTender Pro runtime URL is unavailable.'));

    window[PRO_RUNTIME_KEY] = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.runtimeOwner = 'assets/gtender-dashboard.js';
      script.onload = function () {
        if (window.GTenderDashboard && typeof window.GTenderDashboard.boot === 'function') {
          window.GTenderDashboard.boot(document);
          resolve(window.GTenderDashboard);
        } else {
          reject(new Error('GTender Pro runtime did not register.'));
        }
      };
      script.onerror = function () {
        reject(new Error('GTender Pro runtime failed to load.'));
      };
      document.head.appendChild(script);
    }).finally(function () {
      window[PRO_RUNTIME_KEY] = null;
    });
    return window[PRO_RUNTIME_KEY];
  }

  async function readBackendAccess(session) {
    if (!session || typeof session.authenticatedFetch !== 'function') {
      throw new Error('Native authenticated fetch is unavailable.');
    }

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller
      ? window.setTimeout(function () {
          controller.abort();
        }, ACCESS_TIMEOUT_MS)
      : null;

    try {
      var response = await session.authenticatedFetch(ACCESS_URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      });
      var payload = await response.json().catch(function () {
        return {};
      });
      if (!response.ok || payload.success !== true || !payload.data) {
        var error = new Error(
          (payload && payload.error && payload.error.code) || 'gtender_access_unavailable'
        );
        error.status = response.status;
        error.code =
          (payload && payload.error && payload.error.code) || 'gtender_access_unavailable';
        throw error;
      }
      return payload.data;
    } catch (error) {
      if (error && error.name === 'AbortError') {
        var timeout = new Error('GTender access verification timed out.');
        timeout.code = 'gtender_access_timeout';
        timeout.status = 0;
        throw timeout;
      }
      throw error;
    } finally {
      if (timer !== null) window.clearTimeout(timer);
    }
  }

  async function projectAuthenticated(root, runtimeState, session, sequence, options) {
    var access;
    try {
      access = await readBackendAccess(session);
    } catch (error) {
      if (!runtimeState.active || sequence !== runtimeState.sequence || !documentOwns(root)) return;
      if (
        Number(error && error.status) === 401 ||
        (error && error.code === 'littleg_session_required')
      ) {
        show(root, 'guest');
      } else {
        show(root, 'unavailable');
      }
      return;
    }

    if (!runtimeState.active || sequence !== runtimeState.sequence || !documentOwns(root)) return;
    var mode = normalize(access && access.mode);
    if (mode === 'pro' && access.pro_entitled === true) {
      show(root, 'pro');
      root.setAttribute('data-pro-valid-until', access.valid_until || '');
      try {
        await ensureProRuntime();
        if (!runtimeState.active || sequence !== runtimeState.sequence || !documentOwns(root))
          return;
        if (!options || options.retryPrivateReads !== false) {
          root.dispatchEvent(new CustomEvent('gtender-dashboard:pro-ready', { bubbles: true }));
        }
      } catch (_) {
        if (runtimeState.active && sequence === runtimeState.sequence && documentOwns(root)) {
          root.setAttribute('data-dashboard-render-state', 'unavailable');
        }
      }
      return;
    }
    show(root, 'free');
  }

  async function sync(root, runtimeState, options) {
    if (!runtimeState.active || !documentOwns(root)) return;
    runtimeState.sequence += 1;
    var sequence = runtimeState.sequence;
    var session = window.LittleGWebSession;
    if (!session || typeof session.getState !== 'function') {
      show(root, 'unavailable');
      return;
    }

    var current = session.getState();
    var status = normalize(current && current.status);
    if (status === 'guest' || status === 'anonymous') {
      show(root, 'guest');
      return;
    }
    show(root, 'checking');

    try {
      if (typeof session.ready === 'function') await session.ready();
    } catch (_) {}
    if (!runtimeState.active || sequence !== runtimeState.sequence || !documentOwns(root)) return;

    current = session.getState();
    status = normalize(current && current.status);
    if (status === 'authenticated') {
      await projectAuthenticated(root, runtimeState, session, sequence, options);
      return;
    }
    if (status === 'guest' || status === 'anonymous') {
      show(root, 'guest');
      return;
    }
    if (status === 'booting' || status === 'authenticating') {
      show(root, 'checking');
      return;
    }
    show(root, 'unavailable');
  }

  function mount(root) {
    if (!root || !documentOwns(root) || stateByRoot.has(root)) return;
    var runtimeState = {
      active: true,
      sequence: 0,
      aborter: typeof AbortController === 'function' ? new AbortController() : null,
      onSessionChange: null,
      onPageShow: null,
    };
    stateByRoot.set(root, runtimeState);
    mounted.push(root);
    root.setAttribute('data-access-version', VERSION);
    show(root, 'checking');

    runtimeState.onSessionChange = function () {
      sync(root, runtimeState);
    };
    runtimeState.onPageShow = function () {
      sync(root, runtimeState);
    };

    if (runtimeState.aborter) {
      document.addEventListener('littleg:web-session-change', runtimeState.onSessionChange, {
        signal: runtimeState.aborter.signal,
      });
      window.addEventListener('pageshow', runtimeState.onPageShow, {
        signal: runtimeState.aborter.signal,
      });
    } else {
      document.addEventListener('littleg:web-session-change', runtimeState.onSessionChange);
      window.addEventListener('pageshow', runtimeState.onPageShow);
    }
    sync(root, runtimeState);
  }

  function destroy(root) {
    var runtimeState = root ? stateByRoot.get(root) : null;
    if (!runtimeState) return;
    runtimeState.active = false;
    runtimeState.sequence += 1;
    if (runtimeState.aborter) runtimeState.aborter.abort();
    else {
      if (runtimeState.onSessionChange)
        document.removeEventListener('littleg:web-session-change', runtimeState.onSessionChange);
      if (runtimeState.onPageShow) window.removeEventListener('pageshow', runtimeState.onPageShow);
    }
    stateByRoot.delete(root);
    mounted = mounted.filter(function (candidate) {
      return candidate !== root;
    });
    root.removeAttribute('data-access-version');
  }

  function cleanupDisconnected() {
    mounted.slice().forEach(function (root) {
      if (!documentOwns(root)) destroy(root);
    });
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    list(target, ROOT_SELECTOR).forEach(mount);
    if (!observer && typeof MutationObserver === 'function') {
      observer = new MutationObserver(function () {
        cleanupDisconnected();
        list(document, ROOT_SELECTOR).forEach(mount);
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function refresh(root, options) {
    var runtimeState = root ? stateByRoot.get(root) : null;
    if (!runtimeState || !runtimeState.active || !documentOwns(root)) return Promise.resolve();
    return sync(root, runtimeState, options);
  }

  function destroyAll(scope) {
    var target = scope || document;
    mounted.slice().forEach(function (root) {
      if (target === document || root === target || (target.contains && target.contains(root)))
        destroy(root);
    });
  }

  var api = Object.freeze({ version: VERSION, boot: boot, refresh: refresh, destroy: destroyAll });
  window[GLOBAL_KEY] = api;
  window.GTenderDashboardAccess = api;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})();
